import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Shield, ArrowLeft, User, AlertTriangle } from 'lucide-react';
import { authService } from '../modules/auth/services/authService';
import { getUserRoles } from '../components/auth/RoleBasedProtectedRoute';

const UnauthorizedAccessPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = authService.getCurrentUser();
  const selectedRole = localStorage.getItem('selectedRole');
  const userRoles = getUserRoles(currentUser);
  
  // Get error message from navigation state
  const errorMessage = location.state?.error || "You don't have permission to access this area.";

  const handleGoBack = () => {
    // If user has roles, go to role selection
    if (userRoles.length > 0) {
      navigate('/auth/role-selection');
    } else {
      // If no roles, logout and go to login
      authService.logout();
    }
  };

  const handleGoToAllowedModule = () => {
    // Navigate to a module the user actually has access to
    if (userRoles.includes('doctor')) {
      localStorage.setItem('selectedRole', 'doctor');
      navigate('/doctor/dashboard');
    } else if (userRoles.includes('clinician')) {
      localStorage.setItem('selectedRole', 'clinician');
      navigate('/clinician/dashboard');
    } else if (userRoles.includes('patient')) {
      localStorage.setItem('selectedRole', 'patient');
      navigate('/patient/dashboard');
    } else if (userRoles.includes('admin')) {
      localStorage.setItem('selectedRole', 'admin');
      navigate('/admin/dashboard');
    } else {
      // User has no valid roles
      authService.logout();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
        {/* Icon */}
        <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <Shield className="w-10 h-10 text-red-600" />
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Access Denied
        </h1>

        {/* Error Message */}
        <p className="text-gray-600 mb-6">
          {errorMessage}
        </p>

        {/* User Info */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
          <div className="flex items-center mb-2">
            <User className="w-4 h-4 text-gray-500 mr-2" />
            <span className="text-sm font-medium text-gray-700">Current User:</span>
          </div>
          <p className="text-sm text-gray-600 mb-2">
            {currentUser?.first_name} {currentUser?.last_name} ({currentUser?.email})
          </p>
          
          <div className="flex items-center mb-2">
            <AlertTriangle className="w-4 h-4 text-amber-500 mr-2" />
            <span className="text-sm font-medium text-gray-700">Attempted Role:</span>
          </div>
          <p className="text-sm text-amber-600 mb-2 capitalize">
            {selectedRole || 'Unknown'}
          </p>

          <div className="flex items-center mb-2">
            <Shield className="w-4 h-4 text-green-500 mr-2" />
            <span className="text-sm font-medium text-gray-700">Your Available Roles:</span>
          </div>
          <p className="text-sm text-green-600">
            {userRoles.length > 0 ? userRoles.map(role => role.charAt(0).toUpperCase() + role.slice(1)).join(', ') : 'None'}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          {userRoles.length > 0 && (
            <button
              onClick={handleGoToAllowedModule}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors font-medium"
            >
              Go to My Dashboard
            </button>
          )}
          
          <button
            onClick={handleGoBack}
            className="w-full bg-gray-100 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-200 transition-colors font-medium flex items-center justify-center"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {userRoles.length > 0 ? 'Choose Different Role' : 'Back to Login'}
          </button>
        </div>

        {/* Additional Info */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <p className="text-xs text-gray-500">
            If you believe this is an error, please contact your system administrator.
          </p>
        </div>
      </div>
    </div>
  );
};

export default UnauthorizedAccessPage;
